import {AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {NgModel} from '@angular/forms';
import {OptionModel} from '../../../../../../models/option-model';
import {Subscription} from 'rxjs';
import {AutoSaveService} from '../../../../../../services/auto-save.service';
import {OtherSensorModel} from '../../../../../../models/other-sensor.model';
import {PumpPressureSensorIdService} from '../../../../../../services/pump-pressure-sensor-id.service';
import {PfcSensorOptionsEnum} from '../../../../../../enums/pfc-sensor-options.enum';
import {PfcSensorNameEnum} from '../../../../../../enums/pfc-sensor-name.enum';
import {ShareService} from "../../../../../../services/share.service";
import {ValidationService} from "../../../../../../services/validation.service";
import {debounceTime} from "rxjs/operators";
import {SensorsOtherModel} from '../../../../../../models/data-models/sensors-other-model';
import {GeneralGatewayModel} from '../../../../../../models/data-models/general-gateway-model';
import {OtherSensorNameEnum} from "../../../../../../enums/other-sensor-name-enum";
import {OtherSensorOptionEnum} from "../../../../../../enums/other-sensor-option-enum";
import {DeviceDataService} from "../../../../../../services/device-data.service";
import {GatewayType, IDeviceData, IUvsModule} from "../../../../../../interfaces/juconnGateway.response";

@Component({
  selector: 'app-install-sensors-other',
  templateUrl: './install-sensors-other.component.html',
  styleUrls: ['./install-sensors-other.component.scss'],

})
export class InstallSensorsOtherComponent implements OnInit, AfterViewInit, OnDestroy {
  public model: SensorsOtherModel = new SensorsOtherModel();
  public generalGatewayModel: GeneralGatewayModel = new GeneralGatewayModel();
  public sensorIdOptions: OptionModel[];

  public readonly LOW_IMAGE_QUALITY = AutoSaveService.LOW_IMAGE_QUALITY;
  public moduleIds: number[] = [];
  private deviceData: IDeviceData | null = null;
  private readonly UVS_MODULE_TYPES: GatewayType[] = [GatewayType.SM01, GatewayType.SM02, GatewayType.SM03, GatewayType.SM04, GatewayType.SM05];
  public allOtherSensorTypeOptions: OptionModel[] = [
    {value: OtherSensorOptionEnum.LIGHT, label: OtherSensorNameEnum.LIGHT},
    {value: OtherSensorOptionEnum.LEAKAGE_1, label: OtherSensorNameEnum.LEAKAGE_1},
    {value: OtherSensorOptionEnum.LEAKAGE_2, label: OtherSensorNameEnum.LEAKAGE_2},
    {value: OtherSensorOptionEnum.SHAFT_CONTACT_1, label: OtherSensorNameEnum.SHAFT_CONTACT_1},
    {value: OtherSensorOptionEnum.SHAFT_CONTACT_2, label: OtherSensorNameEnum.SHAFT_CONTACT_2},
    {value: OtherSensorOptionEnum.SWITCHABLE_OUTPUT_1, label: OtherSensorNameEnum.SWITCHABLE_OUTPUT_1},
    {value: OtherSensorOptionEnum.SWITCHABLE_OUTPUT_2, label: OtherSensorNameEnum.SWITCHABLE_OUTPUT_2},
  ];

  // Light and leakage sensors are only supported on module 1
  private readonly MODULE_1_ONLY_SENSOR_TYPES: OtherSensorOptionEnum[] = [
    OtherSensorOptionEnum.LIGHT,
    OtherSensorOptionEnum.LEAKAGE_1,
    OtherSensorOptionEnum.LEAKAGE_2,
  ];
  private readonly MODULE_1_ID = 1;

  pfcSensorIdOptions: OptionModel[] = [];

  @ViewChildren(NgModel) inputs: QueryList<NgModel>;

  private inputSubscriptions: Subscription[] = [];
  private subscriptions: Subscription[] = [];


  constructor(
    private pumpPressureSensorIdService: PumpPressureSensorIdService,
    private shareService: ShareService,
    public validationService: ValidationService,
    private deviceDataService: DeviceDataService
  ) {
    this.subscriptions.push(this.pumpPressureSensorIdService.allSensorIdOptionsSubject$.subscribe((sensorIdOptions) => {
      this.sensorIdOptions = this.pumpPressureSensorIdService.getPressureSensorIdOptions();
    }));
    this.subscriptions.push(this.pumpPressureSensorIdService.pumpSensorIdOptionsSubject$.subscribe((sensorIdOptions) => {
      this.sensorIdOptions = this.pumpPressureSensorIdService.getPressureSensorIdOptions();
    }));
    this.subscriptions.push(this.shareService.sensorsOther$.subscribe((data) => {
      this.model = data || new SensorsOtherModel();
    }));
    this.model = this.shareService.sensorsOther || new SensorsOtherModel();

    this.subscriptions.push(this.shareService.generalGateway$.subscribe((data) => {
      this.generalGatewayModel = data || new GeneralGatewayModel();
      this.moduleIds = this.getUvsModuleIds();
      this.setUpPfcModuleIdOptions();
    }));
    this.generalGatewayModel = this.shareService.generalGateway || new GeneralGatewayModel();
    this.moduleIds = this.getUvsModuleIds();
    this.setUpPfcModuleIdOptions();

    this.subscriptions.push(this.deviceDataService.deviceData$.subscribe(data => {
      this.deviceData = data?.deviceData || null;
    }));
    this.deviceData = this.deviceDataService.getDeviceData()?.deviceData || null;
  }


  async ngOnInit(): Promise<void> {
    this.moduleIds = this.getUvsModuleIds();
    this.setUpPfcModuleIdOptions();

    this.sensorIdOptions = this.pumpPressureSensorIdService.getPressureSensorIdOptions();
  }

  ngAfterViewInit(): void {
    this.inputs.forEach(input => {
      this.inputSubscriptions.push(input.control.valueChanges.pipe(
        debounceTime(500)
      ).subscribe(() => {
        this.shareService.sensorsOther = this.model;
      }));
    });

    // Subscribe to changes in the list of inputs
    // (it is called if some input is added or removed from the form/page,
    // for example, because of *ngIf condition changes)
    this.inputs.changes.subscribe(() => {
      // Unsubscribe from old subscriptions
      this.inputSubscriptions.forEach(subscription => subscription.unsubscribe());

      // Subscribe to new inputs
      this.inputs.forEach(input => {
        this.inputSubscriptions.push(input.control.valueChanges.pipe(
          debounceTime(500)
        ).subscribe(() => {
          this.shareService.sensorsOther = this.model;
        }));
      });
    });
  }

  // Other Sensors

  public addOtherSensor() {
    if (!this.model.otherSensors) {
      this.model.otherSensors = [];
    }
    this.model.otherSensors.push({
      uvsModuleId: null,
      sensorType: undefined,
      image: null
    });
  }

  public updateSensorId(value: number, index: number) {
    const sensor = this.model.otherSensors[index];
    sensor.uvsModuleId = value;
    sensor.sensorType = undefined;
  }

  public onRemoveBtnClicked(index: number) {
    this.model.otherSensors.splice(index, 1);
    this.shareService.sensorsOther = this.model;
  }

  public getSensorTypesStillAvailableForModule(moduleId: number, index: number): OptionModel[] {
    let filteredModel: OptionModel[] = [...this.allOtherSensorTypeOptions];
    if (moduleId === null || moduleId === undefined) {
      return filteredModel;
    }
    // Filter out sensor types already used by another row on the same module
    for (let i = 0; i < this.model.otherSensors.length; i++) {
      if (i !== index) {
        const sensor = this.model.otherSensors[i];
        if (sensor.uvsModuleId === moduleId) {
          const idx = filteredModel.findIndex((option) => option.value === sensor.sensorType);
          if (idx > -1) {
            filteredModel.splice(idx, 1);
          }
        }
      }
    }
    if (moduleId !== this.MODULE_1_ID) {
      filteredModel = filteredModel.filter(option =>
        !this.MODULE_1_ONLY_SENSOR_TYPES.includes(option.value as OtherSensorOptionEnum)
      );
    }
    // If raw data is available, keep only sensor types present in the module's raw data
    if (this.deviceData) {
      const rawModule = this.deviceData.uvsModule.find(m => m.moduleInfo.address === moduleId);
      if (rawModule) {
        filteredModel = filteredModel.filter(option =>
          this.moduleHasSensorType(rawModule, option.value as OtherSensorOptionEnum)
        );
      }
    }
    return filteredModel;
  }

  public getMaxOtherSensorCount(): number {
    return this.moduleIds.reduce((count, moduleId) =>
      count + (moduleId === this.MODULE_1_ID
        ? this.allOtherSensorTypeOptions.length
        : this.allOtherSensorTypeOptions.length - this.MODULE_1_ONLY_SENSOR_TYPES.length), 0);
  }

  public updateSensorType(value: OtherSensorOptionEnum, index: number) {
    this.model.otherSensors[index].sensorType = value;
    this.model.otherSensors[index].sensorOn = undefined;
  }

  private moduleHasSensorType(module: IUvsModule, sensorType: OtherSensorOptionEnum): boolean {
    switch (sensorType) {
      case OtherSensorOptionEnum.LIGHT:
        return module.light?.length > 0;
      case OtherSensorOptionEnum.LEAKAGE_1:
        return module.leakage?.length >= 1;
      case OtherSensorOptionEnum.LEAKAGE_2:
        return module.leakage?.length >= 2;
      case OtherSensorOptionEnum.SHAFT_CONTACT_1:
        return module.pfc?.length >= 1;
      case OtherSensorOptionEnum.SHAFT_CONTACT_2:
        return module.pfc?.length >= 2;
      default:
        return true;
    }
  }

  private getUvsModuleIds(): number[] {
    return this.generalGatewayModel?.uvsModules
      ?.filter(module => this.UVS_MODULE_TYPES.includes(module.uvsType))
      ?.map(module => module.address) || [];
  }

  public isSensorAShaftContact(sensor: OtherSensorModel): boolean {
    return sensor.sensorType === OtherSensorOptionEnum.SHAFT_CONTACT_1 || sensor.sensorType === OtherSensorOptionEnum.SHAFT_CONTACT_2;
  }

  // Pressure Sensors

  public onRemovePressureBtnClicked(index: number) {
    this.model.pressureSensors.splice(index, 1);
    this.shareService.sensorsOther = this.model;
    this.pumpPressureSensorIdService.setPressureSensorIdOptions(this.collectUsedPressureSenorIds());
  }

  public updatePressureSensorId(value: string, index: number) {
    const sensor = this.model.pressureSensors[index];
    sensor.sensorId = value;
    this.shareService.sensorsOther = this.model;
    this.pumpPressureSensorIdService.setPressureSensorIdOptions(this.collectUsedPressureSenorIds());
  }

  public getPressureSensorIdOptions(index: number): OptionModel[] {
    const filteredModel: OptionModel[] = [];
    if (this.sensorIdOptions?.length > 0) {
      this.sensorIdOptions.forEach((option) => {
        filteredModel.push(option);
      });
    }
    if (this.model?.pressureSensors?.length > 0) {
      for (let i = 0; i < this.model.pressureSensors.length; i++) {
        if (i !== index) {
          const sensorIdToRemove = filteredModel.findIndex(option => option.value === this.model.pressureSensors[i].sensorId);
          if (sensorIdToRemove > -1) {
            filteredModel.splice(sensorIdToRemove, 1);
          }
        }
      }
    }
    return filteredModel;
  }

  public addPressureSensor() {
    if (!this.model.pressureSensors) {
      this.model.pressureSensors = [];
    }
    this.model.pressureSensors.push({
      sensorId: null,
      value: null,
      upperLimit: null,
      lowerLimit: null,
      image: null
    });
  }

  showError(model: NgModel) {
    return model.control.touched && model.control.invalid && model.control?.errors?.required;
  }

  isSomeSensorIdAvailableForPressureSensor() {
    if (this.model?.pressureSensors?.length >= 0) {
      return this.sensorIdOptions?.length > 0 && this.model.pressureSensors.length < this.moduleIds.length
    } else {
      return this.sensorIdOptions?.length > 0 && this.moduleIds.length >= 1;
    }
  }

  updateValidity(model: NgModel) {
    setTimeout(_ => {
      this.inputs.forEach(dInput => {
        model.control.updateValueAndValidity();
      });
    }, 0);
  }

  ngOnDestroy(): void {
    this.inputSubscriptions?.forEach(subscription => subscription.unsubscribe());
    this.subscriptions?.forEach(subscription => subscription.unsubscribe());
  }

  private collectUsedPressureSenorIds(): OptionModel[] {
    const usedSensorIds: OptionModel[] = [];
    if (this.model?.pressureSensors?.length > 0) {
      this.model.pressureSensors.forEach(sensor => {
        usedSensorIds.push({value: sensor.sensorId, label: sensor.sensorId});
      });
    }
    return usedSensorIds;
  }

  // PFC Sensors

  public addPfcSensor() {
    if (!this.model.pfcSensors) {
      this.model.pfcSensors = [];
    }
    if (this.model.pfcSensors.length < 4 * this.moduleIds.length) {
      this.model.pfcSensors.push({
        uvsModuleId: null,
        sensorId: null,
        name: PfcSensorOptionsEnum.POTENTIAL_FREE_CONTACT + (this.model.pfcSensors.length + 1),
        closed: false,
        image: null
      });
    }
  }

  public onRemovePfcBtnClicked(index: number) {
    this.model.pfcSensors.splice(index, 1);
    if (this.model.pfcSensors.length > 0) {
      for (let i = 0; i < this.model.pfcSensors.length; i++) {
        this.model.pfcSensors[i].name = PfcSensorOptionsEnum.POTENTIAL_FREE_CONTACT + (i + 1);
      }
    }
    this.shareService.sensorsOther = this.model;
  }


  public getFilteredSensorIds(index: number) {
    const filteredModel: OptionModel[] = [];
    if (this.pfcSensorIdOptions?.length > 0) {
      this.pfcSensorIdOptions.forEach((option) => {
        filteredModel.push(option);
      });
    }
    for (let i = 0; i < this.model.pfcSensors.length; i++) {
      if (i !== index) {
        const sensorIdToRemove = filteredModel.findIndex((option) => option.value === this.model.pfcSensors[i].sensorId);
        if (sensorIdToRemove !== -1) {
          filteredModel.splice(sensorIdToRemove, 1);
        }
      }
    }
    return filteredModel;
  }

  public updatePfcSensorId(value: string, index: number) {
    const sensor = this.model.pfcSensors[index];

    sensor.sensorId = value;
    sensor.uvsModuleId = value ? Number.parseInt(value.split('.')[0]) : null;

    console.log('updated pfc', this.sensorIdOptions, this.model.pfcSensors)
  }

  public getPfcSensorName(index: number): string {
    const sensor = this.model.pfcSensors[index];
    return PfcSensorNameEnum.POTENTIAL_FREE_CONTACT + (index + 1);
  }

  private setUpPfcModuleIdOptions(): void {
    this.pfcSensorIdOptions = [];
    this.moduleIds.forEach((moduleId) => {
      for (let i = 1; i <= 4; i++) {
        this.pfcSensorIdOptions.push({
          value: moduleId + '.' + i,
          label: moduleId + '.' + i
        });
      }
    });
  }
}
